import { FC, useState } from "react";
import { LineStylePopup } from "shared/ui";
import type { StochasticOscillatorSettings, StochasticLevelStyle, IndicatorLineStyle } from "../../model";
import tabStyles from "./ADXStylesTab.module.css";
import inputStyles from "./AcceleratorOscillatorInputsTab.module.css";

type LineKey = "kLine" | "dLine";
type LevelKey = "upperLevel" | "lowerLevel";
type PopupKey = LineKey | LevelKey;

const LINES: { key: LineKey; label: string }[] = [
  { key: "kLine", label: "K Period" },
  { key: "dLine", label: "D Period" },
];
const LEVELS: { key: LevelKey; label: string }[] = [
  { key: "upperLevel", label: "Upper Level" },
  { key: "lowerLevel", label: "Lower Level" },
];

interface Props {
  settings: StochasticOscillatorSettings;
  onChange: (s: StochasticOscillatorSettings) => void;
}

export const StochasticOscillatorStylesTab: FC<Props> = ({ settings, onChange }) => {
  const [openPopup, setOpenPopup] = useState<PopupKey | null>(null);
  const [levelValues, setLevelValues] = useState<Record<LevelKey, string>>({
    upperLevel: String(settings.upperLevel.value),
    lowerLevel: String(settings.lowerLevel.value),
  });

  const toggle = (key: PopupKey) => setOpenPopup((prev) => (prev === key ? null : key));

  const updateLine = (key: LineKey, patch: Partial<IndicatorLineStyle>) =>
    onChange({ ...settings, [key]: { ...settings[key], ...patch } });

  const updateLevel = (key: LevelKey, patch: Partial<StochasticLevelStyle>) =>
    onChange({ ...settings, [key]: { ...settings[key], ...patch } });

  const handleLevelBlur = (key: LevelKey) => {
    const parsed = parseInt(levelValues[key], 10);
    const clamped = isNaN(parsed) ? settings[key].value : Math.min(Math.max(1, parsed), 100);
    setLevelValues((prev) => ({ ...prev, [key]: String(clamped) }));
    updateLevel(key, { value: clamped });
  };

  return (
    <div className={tabStyles.tab}>
      {LINES.map(({ key, label }) => (
        <div key={key} className={tabStyles.row}>
          <div className={tabStyles.left}>
            <label className={tabStyles.checkboxWrap}>
              <input type="checkbox" className={tabStyles.checkboxInput}
                checked={settings[key].visible}
                onChange={(e) => updateLine(key, { visible: e.target.checked })}
              />
              <span className={tabStyles.checkmark} />
            </label>
            <span className={tabStyles.label}>{label}</span>
          </div>
          <div className={tabStyles.btnWrap}>
            <button className={tabStyles.colorBtn} onMouseDown={(e) => e.stopPropagation()} onClick={() => toggle(key)}>
              <span className={tabStyles.colorBtnSwatch} style={{ background: settings[key].color }} />
              <span className={tabStyles.colorBtnWidth}>{settings[key].width}px</span>
            </button>
            {openPopup === key && (
              <LineStylePopup color={settings[key].color} width={settings[key].width}
                onColorChange={(c) => updateLine(key, { color: c })}
                onWidthChange={(w) => updateLine(key, { width: w })}
                onClose={() => setOpenPopup(null)}
              />
            )}
          </div>
        </div>
      ))}
      {LEVELS.map(({ key, label }) => (
        <div key={key} className={tabStyles.row}>
          <div className={tabStyles.left}>
            <label className={tabStyles.checkboxWrap}>
              <input type="checkbox" className={tabStyles.checkboxInput}
                checked={settings[key].visible}
                onChange={(e) => updateLevel(key, { visible: e.target.checked })}
              />
              <span className={tabStyles.checkmark} />
            </label>
            <input type="number" className={inputStyles.input} min={1} max={100}
              value={levelValues[key]}
              onChange={(e) => setLevelValues((prev) => ({ ...prev, [key]: e.target.value }))}
              onBlur={() => handleLevelBlur(key)}
            />
          </div>
          <div className={tabStyles.btnWrap}>
            <button className={tabStyles.colorBtn} onMouseDown={(e) => e.stopPropagation()} onClick={() => toggle(key)}>
              <span className={tabStyles.colorBtnSwatch} style={{ background: settings[key].color }} />
              <span className={tabStyles.colorBtnWidth}>{settings[key].width}px</span>
            </button>
            {openPopup === key && (
              <LineStylePopup color={settings[key].color} width={settings[key].width}
                onColorChange={(c) => updateLevel(key, { color: c })}
                onWidthChange={(w) => updateLevel(key, { width: w })}
                onClose={() => setOpenPopup(null)}
              />
            )}
          </div>
        </div>
      ))}
    </div>
  );
};
